import { Game, type GameResult, type GameStatus, getVariant, reasonText } from '@chessbut/engine';
import type { GameRecordSummary, PlayerInfo, TimeControl } from '@chessbut/shared';
import { useEffect, useMemo, useState } from 'react';
import { Flag, Handshake, RotateCcw, Share2, X, Zap } from 'lucide-react';
import { GameView, type PlayerLabel, verdictFor } from '../components/GameView.tsx';
import { resultShareText } from '../lib/shareText.ts';
import { api } from '../lib/api.ts';
import { timeControlLabel } from '../lib/format.ts';
import { Link } from '../lib/router.tsx';
import { reloadMe, useSession } from '../lib/session.ts';
import { share } from '../lib/share.ts';
import { openAuth } from '../lib/ui.ts';
import { type OnlineGame, client, useOnline } from '../online/client.ts';
import { NotFound } from './Static.tsx';
import { SITE_URL } from '../lib/platform.ts';

interface GameRecord extends GameRecordSummary {
  startFen: string;
  moves: string[];
  timeControl: TimeControl;
  live: boolean;
  yours: boolean;
}

const ratingLabel = (p: PlayerInfo) => (p.rating === null ? null : `${p.rating}${p.provisional ? '?' : ''}`);

export function OnlineGameScreen({ id }: { id: string }) {
  const online = useOnline();
  const g = online.game?.id === id ? online.game : null;
  const [record, setRecord] = useState<GameRecord | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (g) return;
    let cancelled = false;
    api<{ game: GameRecord }>(`/api/games/${encodeURIComponent(id)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.game.live) client.watch(id);
        else setRecord(res.game);
      })
      .catch(() => !cancelled && setMissing(true));
    return () => {
      cancelled = true;
    };
  }, [id, !!g]);

  useEffect(() => () => {
    const cur = client.state.game;
    if (cur?.id === id && cur.you === null) client.unwatch(id);
  }, [id]);

  if (g) return <LiveGame g={g} />;
  if (record) return <Replay record={record} />;
  if (missing) return <NotFound title="Game not found" text="It may have been aborted before it started." />;
  return (
    <div className="page loading">
      <span className="spinner big" />
    </div>
  );
}

function LiveGame({ g }: { g: OnlineGame }) {
  const { me } = useSession();
  const connected = useOnline().status === 'online';
  const you = g.you;
  const over = g.status.over;
  const opp = you === null ? null : you === 0 ? 1 : 0;
  // The computer never takes draws, so don't offer one.
  const vsBot = opp !== null && !!(opp === 0 ? g.white : g.black).bot;
  const [confirm, setConfirm] = useState<'resign' | 'draw' | null>(null);
  const label = (c: 0 | 1): PlayerLabel => {
    const p = c === 0 ? g.white : g.black;
    return {
      name: p.name,
      username: p.username,
      rating: ratingLabel(p),
      isYou: you === c,
      bot: !!p.bot,
      offline: !g.connected[c] && !over,
      streak: you === c ? me?.streak.current || undefined : undefined,
    };
  };

  useEffect(() => {
    if (over && you !== null) void reloadMe();
  }, [over, you]);

  useEffect(() => {
    if (!confirm) return;
    const t = setTimeout(() => setConfirm(null), 3000);
    return () => clearTimeout(t);
  }, [confirm]);

  const [now, setNow] = useState(performance.now());
  useEffect(() => {
    if (!g.graceDeadline) return;
    const t = setInterval(() => setNow(performance.now()), 1000);
    return () => clearInterval(t);
  }, [g.graceDeadline]);

  const myMoves = you === null ? 0 : g.game.history.filter((r) => r.color === you).length;
  const notices = (
    <>
      {you === null && <div className="notice">Spectating</div>}
      {you !== null && !over && !connected && <div className="notice warn">Connection lost. Reconnecting…</div>}
      {opp !== null && !over && !g.connected[opp] && (
        <div className="notice warn">
          Opponent disconnected.
          {g.graceDeadline && ` They have ${Math.max(0, Math.ceil((g.graceDeadline - now) / 1000))}s to come back.`}
        </div>
      )}
      {opp !== null && !over && g.drawOffer === opp && (
        <div className="notice offer">
          <span>Your opponent offers a draw.</span>
          <div className="row">
            <button className="btn small strong" onClick={() => client.draw('accept')}>
              Accept
            </button>
            <button className="btn small" onClick={() => client.draw('decline')}>
              Decline
            </button>
          </div>
        </div>
      )}
      {you !== null && !over && g.drawOffer === you && <div className="notice">Draw offered. Waiting for your opponent…</div>}
      {opp !== null && over && g.rematchOffer === opp && (
        <div className="notice offer">
          <span>Rematch? Your opponent wants another game.</span>
          <div className="row">
            <button className="btn small strong" onClick={() => client.rematch('accept')}>
              Accept
            </button>
            <button className="btn small" onClick={() => client.rematch('decline')}>
              Decline
            </button>
          </div>
        </div>
      )}
      {opp !== null && over && g.left[opp] && <div className="notice">Your opponent left.</div>}
    </>
  );

  const myChange = you === null || !g.ratings ? null : you === 0 ? g.ratings.white : g.ratings.black;
  const myPoints = you === null || !g.points ? null : you === 0 ? g.points.white : g.points.black;
  const oppName = opp === null ? undefined : (opp === 0 ? g.white : g.black).name;
  const outcome = g.status.winner === undefined ? 'draw' : g.status.winner === you ? 'win' : 'loss';
  const shareText =
    over && you !== null && g.status.reason !== 'aborted'
      ? resultShareText({
          variant: g.variant,
          outcome,
          plies: g.game.ply,
          rating: myChange ? { diff: myChange.diff, after: myChange.after } : null,
          streak: me?.streak.current,
          vs: oppName,
        })
      : undefined;
  const shareGame = () =>
    share({
      kind: 'game',
      text: shareText ?? `${g.white.name} vs ${g.black.name}: chess, but ${g.variant.info.but} ${g.variant.info.emoji}`,
      url: `${SITE_URL}/game/${g.id}`,
    });
  const newOpponent = () => {
    client.leaveGame();
    client.quickMatch(g.variant.id);
  };

  return (
    <GameView
      game={g.game}
      version={g.version}
      white={label(0)}
      black={label(1)}
      perspective={you ?? 0}
      you={you}
      movable={(c) => you === c && !over && connected}
      status={g.status}
      clock={g.clock ? { state: g.clock, at: g.clockAt } : null}
      onMove={(uci) => client.move(uci)}
      mode={`${g.rated ? 'rated' : 'casual'} · ${timeControlLabel(g.timeControl)}`}
      notices={notices}
      actions={
        you === null ? (
          <button className="btn" onClick={shareGame}>
            <Share2 className="icon" />
            Share
          </button>
        ) : !over ? (
          myMoves === 0 ? (
            <button className="btn" onClick={() => client.abort()}>
              <X className="icon" />
              Abort
            </button>
          ) : (
            <>
              {!vsBot && (
                <button
                  className={`btn ${confirm === 'draw' ? 'strong' : ''}`}
                  disabled={g.drawOffer !== null || g.game.ply < 2}
                  onClick={() => {
                    if (confirm === 'draw') {
                      client.draw('offer');
                      setConfirm(null);
                    } else setConfirm('draw');
                  }}
                >
                  <Handshake className="icon" />
                  {confirm === 'draw' ? 'Sure?' : 'Draw'}
                </button>
              )}
              <button
                className={`btn ${confirm === 'resign' ? 'danger' : ''}`}
                onClick={() => {
                  if (confirm === 'resign') {
                    client.resign();
                    setConfirm(null);
                  } else setConfirm('resign');
                }}
              >
                <Flag className="icon" />
                {confirm === 'resign' ? 'Sure?' : 'Resign'}
              </button>
              <button className="btn" onClick={shareGame}>
                <Share2 className="icon" />
                Share
              </button>
            </>
          )
        ) : (
          <>
            <button className="btn" disabled={opp !== null && g.left[opp]} onClick={() => client.rematch(g.rematchOffer === opp ? 'accept' : 'offer')}>
              <RotateCcw className="icon" />
              {g.rematchOffer === you ? 'Offered' : 'Rematch'}
            </button>
            <button className="btn" onClick={newOpponent}>
              <Zap className="icon" />
              New
            </button>
            <button className="btn" onClick={shareGame}>
              <Share2 className="icon" />
              Share
            </button>
          </>
        )
      }
      end={
        over
          ? {
              verdict: verdictFor(g.status, you),
              gains:
                you === null
                  ? undefined
                  : {
                      points: myPoints,
                      rating: myChange ? { diff: myChange.diff, after: myChange.after } : null,
                      streak: me?.streak.current ?? null,
                    },
              share: shareText,
              extra:
                me?.guest && you !== null && g.rated ? (
                  <button className="btn strong block" onClick={() => openAuth('signup', 'Create an account to keep your rating and get on the leaderboards.')}>
                    Save your rating: create an account
                  </button>
                ) : null,
              actions:
                you === null ? (
                  <Link to={`/${g.variant.id}`} className="btn primary big block">
                    Play {g.variant.info.name}
                  </Link>
                ) : (
                  <>
                    <button className="btn primary big block" onClick={shareGame}>
                      <Share2 className="icon" />
                      Share result
                    </button>
                    <div className="two-up">
                      <button className="btn small" disabled={opp !== null && g.left[opp]} onClick={() => client.rematch(g.rematchOffer === opp ? 'accept' : 'offer')}>
                        {g.rematchOffer === opp ? 'Accept rematch' : g.rematchOffer === you ? 'Offered…' : 'Rematch'}
                      </button>
                      <button className="btn small" onClick={newOpponent}>
                        New opponent
                      </button>
                    </div>
                  </>
                ),
            }
          : null
      }
    />
  );
}

function Replay({ record }: { record: GameRecord }) {
  const variant = getVariant(record.variant);
  const { me } = useSession();
  const game = useMemo(() => {
    if (!variant) return null;
    try {
      return Game.fromMoves(variant, record.moves);
    } catch {
      return null;
    }
  }, [record, variant]);
  if (!variant || !game) return <NotFound title="Can't show this game" />;
  const status: GameStatus = record.result
    ? { over: true, result: record.result, reason: record.reason ?? undefined, winner: winnerOf(record.result) }
    : { over: true, reason: record.reason ?? 'aborted' };
  const side = (s: GameRecordSummary['white']): PlayerLabel => ({
    name: s.name,
    username: s.username,
    rating: s.rating === null ? null : `${s.rating}${s.diff !== null ? ` (${s.diff >= 0 ? '+' : ''}${s.diff})` : ''}`,
    isYou: !!me && s.id === me.id,
  });
  const you = me ? (record.white.id === me.id ? 0 : record.black.id === me.id ? 1 : null) : null;
  return (
      <GameView
        game={game}
        version={0}
        white={side(record.white)}
        black={side(record.black)}
        perspective={you ?? 0}
        you={you}
        movable={() => false}
        status={status}
        onMove={() => {}}
        mode={`replay · ${record.mode === 'bot' ? `bot level ${record.botLevel}` : record.rated ? 'rated' : 'casual'}`}
        notices={<div className="notice">{reasonText(variant, status.reason)} · {new Date(record.startedAt).toLocaleDateString()}</div>}
        actions={
          <>
            <button className="btn" onClick={() => share({ kind: 'replay', text: `${record.white.name} vs ${record.black.name}: chess, but ${variant.info.but}`, url: location.href })}>
              <Share2 className="icon" />
              Share
            </button>
            <Link to={`/${variant.id}`} className="btn">
              <Zap className="icon" />
              Play
            </Link>
          </>
        }
        end={null}
      />
  );
}

function winnerOf(result: GameResult) {
  return result === '1-0' ? (0 as const) : result === '0-1' ? (1 as const) : undefined;
}
